import { createTheme } from "@mui/material/styles";

// Glassmorphism tokens (tinted to avoid grey/dirty whites)
const glass = {
  background: "linear-gradient(180deg, rgba(123,97,255,0.08), rgba(97,218,251,0.06))",
  border: "1px solid rgba(123,97,255,0.24)",
  boxShadow: "0 24px 80px rgba(5,4,20,0.55), inset 0 1px 0 rgba(255,255,255,0.10)",
  backdropFilter: "blur(14px) saturate(135%)",
  WebkitBackdropFilter: "blur(14px) saturate(135%)",
};

const glassStrong = {
  background: "linear-gradient(180deg, rgba(123,97,255,0.12), rgba(97,218,251,0.08))",
  border: "1px solid rgba(123,97,255,0.30)",
  boxShadow: "0 28px 100px rgba(5,4,20,0.6), inset 0 1px 0 rgba(255,255,255,0.14)",
  backdropFilter: "blur(18px) saturate(150%)",
  WebkitBackdropFilter: "blur(18px) saturate(150%)",
};

const glassSoft = {
  background: "linear-gradient(180deg, rgba(123,97,255,0.06), rgba(97,218,251,0.05))",
  border: "1px solid rgba(123,97,255,0.18)",
  boxShadow: "0 18px 60px rgba(5,4,20,0.45), inset 0 1px 0 rgba(255,255,255,0.08)",
  backdropFilter: "blur(10px) saturate(125%)",
  WebkitBackdropFilter: "blur(10px) saturate(125%)",
};

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#7b61ff" },
    secondary: { main: "#61dafb" },
    background: { default: "rgba(15, 15, 35, 0.85)" }
  },
  typography: {
    fontFamily: '"Roboto Condensed", "Inter", "Segoe UI", system-ui, -apple-system, Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    button: {
      fontWeight: 500,
    },
    body1: { fontWeight: 300 },
    body2: { fontWeight: 300 },
  },
  shape: { borderRadius: 16 },
  custom: {
    glass,
    glassStrong,
    glassSoft,
    lanes: {
      CCM: { tint: "#7b61ff", stroke: "rgba(123,97,255,0.75)" },
      RPM: { tint: "#61dafb", stroke: "rgba(97,218,251,0.7)" },
      BHI: { tint: "#ff9f43", stroke: "rgba(255,159,67,0.7)" },
      Main: { tint: "#b9bdfc", stroke: "rgba(185,189,252,0.6)" },
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          ...glass,
          borderRadius: 16,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: "rgba(255,255,255,0.12)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderColor: "rgba(123,97,255,0.4)",
          background: "linear-gradient(135deg, rgba(123,97,255,0.22), rgba(97,218,251,0.18))",
          color: "rgba(235,238,255,0.95)",
          fontWeight: 600,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12), 0 8px 22px rgba(6,7,25,0.22)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
        outlined: {
          borderColor: "rgba(123,97,255,0.5)",
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 14,
          fontWeight: 600,
          letterSpacing: 0.2,
        },
        contained: {
          color: "rgba(245,247,255,0.96)",
          background: "linear-gradient(135deg, rgba(123,97,255,0.85), rgba(97,218,251,0.75))",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12), 0 10px 28px rgba(6,7,25,0.32)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: "1px solid rgba(123,97,255,0.5)",
          '&:hover': {
            background: "linear-gradient(135deg, rgba(123,97,255,0.95), rgba(97,218,251,0.85))",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.16), 0 12px 32px rgba(6,7,25,0.4)",
            borderColor: "rgba(123,97,255,0.7)",
          },
          '&:active': {
            transform: "translateY(1px)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18), 0 6px 16px rgba(6,7,25,0.36)",
          }
        },
        outlined: {
          color: "rgba(235,238,255,0.92)",
          borderColor: "rgba(123,97,255,0.6)",
          background: "linear-gradient(135deg, rgba(123,97,255,0.12), rgba(97,218,251,0.10))",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10), 0 8px 22px rgba(6,7,25,0.22)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          '&:hover': {
            borderColor: "rgba(123,97,255,0.8)",
            background: "linear-gradient(135deg, rgba(123,97,255,0.18), rgba(97,218,251,0.16))",
          }
        },
        text: {
          color: "rgba(235,238,255,0.85)",
          '&:hover': {
            backgroundColor: "rgba(123,97,255,0.10)",
          }
        },
        sizeLarge: {
          paddingLeft: 18,
          paddingRight: 18,
          height: 44,
        }
      }
    }
  },
});

export default theme;
